import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, 
  Zap, 
  Star, 
  Waves, 
  Moon,
  ArrowRight,
  Shield,
  LineChart,
  Brain,
  Heart,
  Download
} from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';
import { motion, AnimatePresence } from 'framer-motion';
import { useToolTheme } from '@/contexts/ThemeContext';
import { isMobile } from 'react-device-detect';

export default function Welcome() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toolColors } = useToolTheme();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowInstallButton(false);
      }
    } catch (error) {
      console.error('Error installing app:', error);
    }
  };

  const features = [
    {
      icon: <Brain className="w-6 h-6 text-purple-500" />,
      title: "Diagnostic personnalisé",
      description: "Évaluez votre état énergétique selon votre type HD"
    },
    {
      icon: <LineChart className="w-6 h-6 text-purple-500" />,
      title: "Suivi des tendances",
      description: "Visualisez l'évolution de votre énergie dans le temps"
    },
    {
      icon: <Heart className="w-6 h-6 text-purple-500" />,
      title: "Recommandations adaptées",
      description: "Recevez des conseils personnalisés pour votre bien-être"
    }
  ];

  const energyTypes = [
    { 
      icon: <Sparkles className="w-8 h-8" />, 
      name: "Manifestor", 
      color: "bg-manifestor/10 text-manifestor",
      description: "Les initiateurs naturels, dotés d'une énergie puissante pour créer et impacter. Leur défi est d'informer avant d'agir pour éviter les résistances.",
      traits: ["Énergie indépendante", "Capacité d'initiation", "Impact créatif", "Leadership naturel"]
    },
    { 
      icon: <Zap className="w-8 h-8" />, 
      name: "Generator", 
      color: "bg-generator/10 text-generator",
      description: "Les bâtisseurs du monde, avec une énergie sacrale pure et constante. Leur force est de répondre authentiquement à la vie pour trouver satisfaction.",
      traits: ["Énergie sacrale puissante", "Endurance naturelle", "Capacité de construction", "Satisfaction dans l'action"]
    },
    { 
      icon: <Star className="w-8 h-8" />, 
      name: "Manifesting Generator", 
      color: "bg-manifesting-generator/10 text-manifesting-generator",
      description: "Les multi-potentiels, combinant l'énergie sacrale et la capacité d'initier. Leur talent est de rapidement passer d'un projet à l'autre avec succès.",
      traits: ["Énergie dynamique", "Adaptabilité rapide", "Multi-tâches naturel", "Créativité en action"]
    },
    { 
      icon: <Waves className="w-8 h-8" />, 
      name: "Projector", 
      color: "bg-projector/10 text-projector",
      description: "Les guides et conseillers, dotés d'une vision pénétrante des autres. Leur sagesse s'exprime pleinement quand ils sont reconnus et invités.",
      traits: ["Vision profonde", "Guide naturel", "Sagesse intuitive", "Excellence relationnelle"]
    },
    { 
      icon: <Moon className="w-8 h-8" />, 
      name: "Reflector", 
      color: "bg-reflector/10 text-reflector",
      description: "Les miroirs de la communauté, évaluant et amplifiant l'énergie collective. Leur cycle lunaire de 28 jours guide leur prise de décision.",
      traits: ["Sensibilité unique", "Sagesse lunaire", "Clarté amplificatrice", "Perspective élevée"]
    }
  ];

  return (
    <motion.div 
      className="min-h-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <BackButton to="/" />
      
      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex items-center justify-center overflow-hidden px-4">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=3648&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.3)'
          }}
        />

        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-1"
          style={{
            background: 'linear-gradient(to bottom, rgba(84, 7, 91, 0.9), rgba(147, 51, 234, 0.7))'
          }}
        />

        {/* Animated Blobs */}
        <div className="absolute inset-0 z-1 overflow-hidden">
          <div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute -top-48 left-1/2 w-96 h-96 bg-pink-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-48 left-1/4 w-96 h-96 bg-yellow-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mb-12"
            >
              <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl inline-block">
                <Brain className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Baromètre Énergétique HED
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Suivez et optimisez votre énergie quotidienne selon votre type Human Design
            </p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {!currentUser ? (
                <>
                  <Button
                    onClick={() => navigate('/barometre/register')}
                    className="w-full sm:w-auto bg-white text-purple-900 hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg gap-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Commencer maintenant
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/barometre/login')}
                    className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full"
                  >
                    Se connecter
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => navigate('/barometre/dashboard')}
                  className="w-full sm:w-auto bg-white text-purple-900 hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg gap-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Accéder au tableau de bord
                  <ArrowRight className="w-5 h-5" />
                </Button>
              )}

              {/* Bouton d'installation */}
              {isMobile && showInstallButton && (
                <Button
                  onClick={handleInstall}
                  variant="outline"
                  className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg gap-2 rounded-full"
                >
                  <Download className="w-5 h-5" />
                  Installer l'application
                </Button>
              )}
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full p-1">
            <motion.div 
              className="w-1 h-2 bg-white rounded-full mx-auto"
              animate={{ 
                y: [0, 12, 0],
                opacity: [1, 0.5, 1]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
        </motion.div>
      </div>

      {/* Features Section */}
      <div className="py-16 px-4 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="p-6 bg-white/90 backdrop-blur-sm hover:shadow-lg transition-all h-full">
                  <div className="flex flex-col items-center text-center h-full">
                    <div className="p-3 bg-purple-100 rounded-xl mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-enhanced" style={{ color: toolColors.primary }}>{feature.title}</h3>
                    <p className="text-gray-800 text-enhanced">{feature.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Types Section */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 hero-title"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ color: toolColors.primary }}
          >
            Pour tous les types d'énergie
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {energyTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.1 * index }}
                whileHover={{ scale: 1.02 }}
              >
                <Card className={`p-4 sm:p-6 flex flex-col items-center text-center ${type.color} bg-white/90 hover:shadow-lg transition-all duration-300 h-full`}>
                  <div className="mb-4">
                    {type.icon}
                  </div>
                  <h3 className="font-semibold text-enhanced mb-3">{type.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-4 hover:line-clamp-none transition-all duration-300">
                    {type.description}
                  </p>
                  <div className="mt-auto pt-4">
                    <div className="flex flex-wrap justify-center gap-2">
                      {type.traits.map((trait, i) => (
                        <div key={i} className="text-xs px-2 py-1 rounded-full bg-white/50 inline-block">
                          {trait}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="py-16 px-4 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            className="flex justify-center mb-4"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Shield className="w-8 h-8" style={{ color: toolColors.primary }} />
          </motion.div>
          <motion.h2 
            className="text-xl sm:text-2xl font-semibold mb-4 hero-title"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ color: toolColors.primary }}
          >
            Vos données sont protégées
          </motion.h2>
          <motion.p 
            className="text-base sm:text-lg hero-text"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Nous accordons la plus grande importance à la confidentialité de vos informations personnelles.
            Toutes vos données sont cryptées et sécurisées.
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
}